import {
  TourCategoryLiteModel,
  TourCategoryModel,
  getTourCategoriesKey,
  getTourCategoryKey,
  useGetTourCategories,
  useGetTourCategory,
} from 'api-hooks/tour-category';
import { OnChangeParams, Option } from 'baseui/select';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import { QueryTransformer } from 'common/helpers/common';
import { Input } from 'components/elements';
import { useAuthorization } from 'hooks/use-authorization';
import useNavigation from 'hooks/use-navigation';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

function transformer(item: TourCategoryLiteModel | TourCategoryModel): Option {
  return {
    id: item.id,
    label: item.name,
  };
}

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterSelectItem?: (prevValue?: any, value?: any) => void;
  onAfterChange?: (value?: OnChangeParams) => void;
  onAfterDetailChange?: (value: TourCategoryModel) => void;
  noMargin?: boolean;
  filter?: any;
  fieldType?: 'control' | 'uncontrol';
}

export default function TourCategorySelectInput(props: Props) {
  const { t } = useTranslation();
  const {
    label = t('common:tour_category'),
    placeholder = t('common:select_tour_category'),
    filter,
  } = props;
  const { control, setValue } = useFormContext<any>();
  const value = useWatch({ control, name: props.name });

  const { navigate } = useNavigation();

  const { can } = useAuthorization();

  const { overrides, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: (_value) => {
      setValue(props.name, _value[0]?.id, {
        shouldTouch: true,
        shouldValidate: true,
      });
      props.onAfterSelectItem?.(value, _value[0]?.id);
    },
    useListQueryHook: useGetTourCategories,
    useDetailLazyQueryHook: useGetTourCategory,
    listKeyFunction: getTourCategoriesKey,
    detailKeyFunction: getTourCategoryKey,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
          filter,
        },
      }),
      [filter],
    ),
    renderCreate: !!can(AuthorizationRules.TourCategoriesCreate),
    onClickCreate: React.useCallback(() => {
      navigate(NavigationRoutes.TourCategoryCreate);
      props.onClose?.();
    }, [navigate, props]),
    getMemoizedDetailVariables: React.useCallback(
      (value: string) => ({
        id: value,
      }),
      [],
    ),
    listTransformer: React.useCallback((result) => {
      return result.data.map((v) => transformer(v));
    }, []),
    detailTransformer: React.useCallback(
      (result) => {
        const newResult = QueryTransformer(result, TourCategoryModel);
        props.onAfterDetailChange?.(newResult.data);
        return transformer(newResult.data);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    ),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      fieldType={props.fieldType}
      overrides={overrides}
      {...selectProps}
    />
  );
}
